import { defineStore } from 'pinia'
import { ref } from 'vue'
import { authApi, userApi } from '../services/api'

export const useUserStore = defineStore('user', () => {
  const user = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const isInitialized = ref(false)
  const isFetching = ref(false)
  const fetchPromise = ref(null)

  const accountManagers = ref([])
  const saleManagers = ref([])
  const analyticLeads = ref([])
  const usersAll = ref([])

  const fetchUser = async () => {
    if (isFetching.value && fetchPromise.value) {
      try {
        await fetchPromise.value
        return user.value
      } catch (error) {
        console.error('Ошибка при ожидании загрузки пользователя:', error)
        throw error
      }
    }

    if (isInitialized.value && user.value) {
      return user.value
    }

    isFetching.value = true
    loading.value = true
    error.value = null

    fetchPromise.value = (async () => {
      try {
        const userData = await authApi.getMe()

        user.value = userData
        isInitialized.value = true

        return userData
      } catch (err) {
        error.value = err.message || 'Ошибка загрузки данных пользователя'
        throw err
      } finally {
        loading.value = false
        isFetching.value = false
        fetchPromise.value = null
      }
    })()

    return fetchPromise.value
  }

  const fetchAccountManagers = async () => {
    try {
      const data = await userApi.getList({
        role: 'ROLE_ACCOUNT_MANAGER',
        limit: 100
      })
      accountManagers.value = data.users || data || []
    } catch (e) {
      accountManagers.value = []
    }
  }

  const fetchSaleManagers = async () => {
    try {
      const data = await userApi.getList({
        role: 'ROLE_SALE_MANAGER',
        limit: 100
      })
      saleManagers.value = data.users || data || []
    } catch (e) {
      saleManagers.value = []
    }
  }

  const fetchAnalyticLeads = async () => {
    try {
      const data = await userApi.getList({
        role: 'ROLE_ANALYTIC_LEAD',
        limit: 100
      })
      analyticLeads.value = data.users || data || []
    } catch (e) {
      analyticLeads.value = []
    }
  }

  const fetchAllUsers = async () => {
    try {
      const data = await userApi.getList({ limit: 1000 })
      usersAll.value = data.users || data || []
    } catch (e) {
      usersAll.value = []
    }
  }

  const fetchUsersByRole = async () => {
    try {
      const data = await userApi.getList({
        role: 'ROLE_USER',
        limit: 9999,
        page: 1
      })

      return data.users || data || []
    } catch (e) {
      return []
    }
  }

  function getUserFullNameById(id) {
    const user =
      usersAll.value.find(u => u.id == id) ||
      accountManagers.value.find(u => u.id == id) ||
      saleManagers.value.find(u => u.id == id) ||
      analyticLeads.value.find(u => u.id == id)
    if (!user) return id || ''
    const { first_name, middle_name, middle_mame, surname } = user
    const middle = middle_name || middle_mame || ''

    return [surname, first_name, middle].filter(Boolean).join(' ')
  }

  function getUserFullNameByIdLocal(id) {
    const user =
      accountManagers.value.find(u => u.id == id) ||
      saleManagers.value.find(u => u.id == id) ||
      analyticLeads.value.find(u => u.id == id)
    if (!user) return id || ''
    const { first_name, middle_name, middle_mame, surname } = user
    const middle = middle_name || middle_mame || ''
    return [surname, first_name, middle].filter(Boolean).join(' ')
  }

  const clearUser = () => {
    user.value = null
    error.value = null
    isInitialized.value = false
    isFetching.value = false
    fetchPromise.value = null
  }

  const getUserFullName = () => {
    if (!user.value) return ''

    const { first_name, middle_name, surname } = user.value
    const parts = [surname, first_name, middle_name].filter(Boolean)

    return parts.join(' ')
  }

  const hasRole = (role, scope = 'global') => {
    const roles = user.value?.permissions?.[scope]?.roles

    if (!Array.isArray(roles)) return false

    return roles.includes(role)
  }

  return {
    user,
    loading,
    error,
    isInitialized,
    fetchUser,
    clearUser,
    getUserFullName,
    hasRole,
    accountManagers,
    saleManagers,
    analyticLeads,
    fetchAccountManagers,
    fetchSaleManagers,
    fetchAnalyticLeads,
    usersAll,
    fetchAllUsers,
    fetchUsersByRole,
    getUserFullNameById,
    getUserFullNameByIdLocal
  }
})
